import React, { useEffect, useMemo, useRef, useState } from 'react';
import { familyTreesAPI, peopleAPI, PersonDto, UpdatePersonRequest } from '../services/api';
import { Button, IconButton, ModalFooter, ModalHeader, SegmentedControl, formInputClassName } from './common';
import { PhotoUploadField } from './PhotoUploadField';
import { CropData } from './ImageCropModal';
import { UnsavedChangesDialog } from './UnsavedChangesDialog';
import { brandClasses } from '../theme';
import { getDateInputValue, isBirthDateAfterDeathDate, serializeDateInputToIso } from '../utils/personDateUtils';
import { normalizeRelationshipType } from '../utils/relationshipLabels';
import { trackEvent } from '../services/analytics';
import { useTranslation } from 'react-i18next';

export interface EditPersonSuccessResult {
  didChange: boolean;
  person: PersonDto;
  photoChanged: boolean;
}

export interface PersonFamilyLink {
  relationshipId: string;
  relatedPersonId: string;
  relatedPersonName: string;
  /** Raw relationship type string from the API (e.g. "Parent", "Spouse"). Used for grouping/sorting. */
  relationshipType: string;
  /** Translated display label for the relationship, shown in the UI. */
  relationToPerson: string;
  canEdit: boolean;
  canDelete: boolean;
}

interface EditPersonModalProps {
  treeId: string;
  person: PersonDto;
  familyLinks?: PersonFamilyLink[];
  onClose: () => void;
  onManageFamilyLinks?: () => void;
  onAddExistingFamilyLink?: () => void;
  onAddFamilyLinkByType?: (relationshipType: string) => void;
  onQuickAddFamilyLink?: () => void;
  onJumpToFamilyLink?: (relationshipId: string) => void;
  onEditFamilyLink?: (relationshipId: string) => void;
  onSuccess: (result: EditPersonSuccessResult) => void;
}

type EditableFormData = {
  title: string;
  firstName: string;
  middleName: string;
  lastName: string;
  suffix: string;
  gender: string;
  dateOfBirth: string;
  dateOfDeath: string;
  placeOfBirth: string;
  placeOfDeath: string;
  restingPlace: string;
  address1: string;
  address2: string;
  city: string;
  state: string;
  zip: string;
  country: string;
  phone: string;
  shortBio: string;
  longStory: string;
  comments: string;
  photoUrl: string;
  isDeceased: boolean;
  publicVisibility: 'Public' | 'Masked';
  allowPublicDisplayForLivingPerson: boolean;
};

const sectionLabelClassName = 'text-[11px] font-medium uppercase tracking-[0.08em] text-[#185FA5]';
const fieldLabelClassName = 'text-[11px] font-medium uppercase tracking-[0.05em] text-[#6B6560]';
const inputClassName = formInputClassName;
const dateInputWrapperClassName = 'relative';
const clearDateButtonClassName =
  'absolute right-2 top-1/2 -translate-y-1/2 rounded-md px-2 py-1 text-[11px] font-medium text-[#6B6560] transition hover:bg-[#EEF4FB] hover:text-[#185FA5]';
const storyPromptButtonClassName =
  'rounded-full border border-[#C8C3BB] bg-[#FFFFFF] px-3 py-1.5 text-xs font-medium text-[#1A1714] transition hover:bg-[#EEF4FB] hover:border-[#185FA5]';

const FAMILY_LINK_GROUP_ORDER = [
  'Partner',
  'Parents & Elders',
  'Children & Descendants',
  'Siblings & Extended Family',
  'Other Connections',
] as const;

const STORY_PROMPTS = [
  {
    label: 'Early Life',
    key: 'earlyLife',
    template: 'Early Life\n\nShare origins, birthplace, and the family setting that shaped this person.\n',
  },
  {
    label: 'Milestones',
    key: 'milestones',
    template: 'Life Milestones\n\nCapture important turning points, achievements, and transitions.\n',
  },
  {
    label: 'Family Memory',
    key: 'familyMemory',
    template: 'Family Memory\n\nRecord a meaningful memory, tradition, or story passed down.\n',
  },
  {
    label: 'Legacy',
    key: 'legacy',
    template: 'Legacy\n\nDescribe how this person is remembered and the impact they left behind.\n',
  },
] as const;

const getFamilyLinkGroupLabel = (relationshipType: string): (typeof FAMILY_LINK_GROUP_ORDER)[number] => {
  const normalized = normalizeRelationshipType(relationshipType);
  switch (normalized) {
    case 'spouse':
    case 'inlaw':
      return 'Partner';
    case 'parent':
    case 'stepfather':
    case 'stepmother':
    case 'grandparent':
    case 'aunt':
    case 'uncle':
      return 'Parents & Elders';
    case 'child':
    case 'grandchild':
    case 'niecenephew':
      return 'Children & Descendants';
    case 'sibling':
    case 'stepsibling':
    case 'cousin':
      return 'Siblings & Extended Family';
    default:
      return 'Other Connections';
  }
};

export const EditPersonModal: React.FC<EditPersonModalProps> = ({
  treeId,
  person,
  familyLinks = [],
  onClose,
  onManageFamilyLinks,
  onAddExistingFamilyLink,
  onAddFamilyLinkByType,
  onQuickAddFamilyLink,
  onJumpToFamilyLink,
  onEditFamilyLink,
  onSuccess,
}) => {
  const { t } = useTranslation();
  const familyGroupLabels: Record<(typeof FAMILY_LINK_GROUP_ORDER)[number], string> = {
    'Partner': t('person.familyGroups.partner', { ns: 'canvas' }),
    'Parents & Elders': t('person.familyGroups.parentsElders', { ns: 'canvas' }),
    'Children & Descendants': t('person.familyGroups.childrenDescendants', { ns: 'canvas' }),
    'Siblings & Extended Family': t('person.familyGroups.siblingsExtended', { ns: 'canvas' }),
    'Other Connections': t('person.familyGroups.otherConnections', { ns: 'canvas' }),
  };
  const extractPayload = <T,>(payload: T | { data?: T | { data?: T } }): T => {
    if (typeof payload === 'object' && payload !== null && 'data' in payload) {
      const wrapped = payload as { data?: T | { data?: T } };
      return (typeof wrapped.data === 'object' && wrapped.data !== null && 'data' in wrapped.data
        ? (wrapped.data as { data?: T }).data
        : wrapped.data) as T;
    }

    return payload as T;
  };

  const initialDateOfBirth = getDateInputValue(person.dateOfBirth);
  const initialDateOfDeath = getDateInputValue(person.dateOfDeath);
  const initialIsDeceased = Boolean(person.isDeceased || initialDateOfDeath);

  const [formData, setFormData] = useState<EditableFormData>({
    title: person.title || '',
    firstName: person.firstName || '',
    middleName: person.middleName || '',
    lastName: person.lastName || '',
    suffix: person.suffix || '',
    gender: person.gender || '',
    dateOfBirth: initialDateOfBirth,
    dateOfDeath: initialDateOfDeath,
    placeOfBirth: person.placeOfBirth || '',
    placeOfDeath: person.placeOfDeath || '',
    restingPlace: person.restingPlace || '',
    address1: person.address1 || '',
    address2: person.address2 || '',
    city: person.city || '',
    state: person.state || '',
    zip: person.zip || '',
    country: person.country || '',
    phone: person.phone || '',
    shortBio: person.shortBio || '',
    longStory: person.longStory || '',
    comments: person.comments || '',
    photoUrl: person.photoUrl || '',
    isDeceased: initialIsDeceased,
    publicVisibility: person.publicVisibility || 'Public',
    allowPublicDisplayForLivingPerson: person.allowPublicDisplayForLivingPerson || false,
  });
  const [cropData, setCropData] = useState<CropData | null>(
    person.photoCropX !== undefined &&
      person.photoCropY !== undefined &&
      person.photoCropWidth !== undefined &&
      person.photoCropHeight !== undefined &&
      person.photoCropZoom !== undefined
      ? {
          cropX: person.photoCropX,
          cropY: person.photoCropY,
          cropWidth: person.photoCropWidth,
          cropHeight: person.photoCropHeight,
          cropZoom: person.photoCropZoom,
        }
      : null
  );
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [photoChanged, setPhotoChanged] = useState(false);
  const [pendingPhotoFile, setPendingPhotoFile] = useState<File | null>(null);
  const [isAddressExpanded, setIsAddressExpanded] = useState(
    Boolean(person.address1 || person.address2 || person.city || person.state || person.zip || person.country)
  );
  const [isStoryEditorOpen, setIsStoryEditorOpen] = useState(false);
  const [storyDraft, setStoryDraft] = useState(person.longStory || '');
  const [showUnsavedChangesDialog, setShowUnsavedChangesDialog] = useState(false);
  const [livingVisibilityCapability, setLivingVisibilityCapability] = useState<{
    canEnableLivingPersonPublicDisplay: boolean;
    blockedReason?: string | null;
  } | null>(null);
  const pendingExitActionRef = useRef<(() => void) | null>(null);

  const normalizedInitialValues = useMemo(
    () => ({
      title: person.title || '',
      firstName: person.firstName || '',
      middleName: person.middleName || '',
      lastName: person.lastName || '',
      suffix: person.suffix || '',
      gender: person.gender || '',
      dateOfBirth: initialDateOfBirth,
      dateOfDeath: initialDateOfDeath,
      placeOfBirth: person.placeOfBirth || '',
      placeOfDeath: person.placeOfDeath || '',
      restingPlace: person.restingPlace || '',
      address1: person.address1 || '',
      address2: person.address2 || '',
      city: person.city || '',
      state: person.state || '',
      zip: person.zip || '',
      country: person.country || '',
      phone: person.phone || '',
      shortBio: person.shortBio || '',
      longStory: person.longStory || '',
      comments: person.comments || '',
      isDeceased: initialIsDeceased,
      publicVisibility: person.publicVisibility || 'Public',
      allowPublicDisplayForLivingPerson: person.allowPublicDisplayForLivingPerson || false,
    }),
    [initialDateOfBirth, initialDateOfDeath, initialIsDeceased, person]
  );

  const groupedFamilyLinks = useMemo(() => {
    const linksByGroup = familyLinks.reduce<Record<string, PersonFamilyLink[]>>((groups, link) => {
      const groupLabel = getFamilyLinkGroupLabel(link.relationshipType);
      if (!groups[groupLabel]) {
        groups[groupLabel] = [];
      }
      groups[groupLabel].push(link);
      return groups;
    }, {});

    return FAMILY_LINK_GROUP_ORDER
      .filter((groupLabel) => (linksByGroup[groupLabel]?.length ?? 0) > 0)
      .map((groupLabel) => ({
        groupLabel,
        links: linksByGroup[groupLabel],
      }));
  }, [familyLinks]);

  const storyWordCount = useMemo(() => {
    const trimmedStory = formData.longStory.trim();
    return trimmedStory ? trimmedStory.split(/\s+/).length : 0;
  }, [formData.longStory]);

  const hasUnsavedChanges = useMemo(
    () =>
      normalizedInitialValues.title !== formData.title ||
      normalizedInitialValues.firstName !== formData.firstName ||
      normalizedInitialValues.middleName !== formData.middleName ||
      normalizedInitialValues.lastName !== formData.lastName ||
      normalizedInitialValues.suffix !== formData.suffix ||
      normalizedInitialValues.gender !== formData.gender ||
      normalizedInitialValues.dateOfBirth !== formData.dateOfBirth ||
      normalizedInitialValues.dateOfDeath !== formData.dateOfDeath ||
      normalizedInitialValues.placeOfBirth !== formData.placeOfBirth ||
      normalizedInitialValues.placeOfDeath !== formData.placeOfDeath ||
      normalizedInitialValues.restingPlace !== formData.restingPlace ||
      normalizedInitialValues.address1 !== formData.address1 ||
      normalizedInitialValues.address2 !== formData.address2 ||
      normalizedInitialValues.city !== formData.city ||
      normalizedInitialValues.state !== formData.state ||
      normalizedInitialValues.zip !== formData.zip ||
      normalizedInitialValues.country !== formData.country ||
      normalizedInitialValues.phone !== formData.phone ||
      normalizedInitialValues.shortBio !== formData.shortBio ||
      normalizedInitialValues.longStory !== formData.longStory ||
      normalizedInitialValues.comments !== formData.comments ||
      normalizedInitialValues.isDeceased !== formData.isDeceased ||
      normalizedInitialValues.publicVisibility !== formData.publicVisibility ||
      normalizedInitialValues.allowPublicDisplayForLivingPerson !== formData.allowPublicDisplayForLivingPerson ||
      photoChanged ||
      Boolean(pendingPhotoFile) ||
      (isStoryEditorOpen && storyDraft !== formData.longStory),
    [formData, isStoryEditorOpen, normalizedInitialValues, pendingPhotoFile, photoChanged, storyDraft]
  );

  useEffect(() => {
    let isMounted = true;

    familyTreesAPI.getLivingPersonPublicDisplayCapability(treeId)
      .then((response) => {
        if (isMounted) {
          setLivingVisibilityCapability(response.data);
        }
      })
      .catch(() => {
        if (isMounted) {
          setLivingVisibilityCapability(null);
        }
      });

    return () => {
      isMounted = false;
    };
  }, [treeId]);

  const setFieldValue = <K extends keyof EditableFormData>(field: K, value: EditableFormData[K]) => {
    setFormData((current) => ({
      ...current,
      [field]: value,
    }));
  };

  const toggleDeceasedState = (nextValue: boolean) => {
    setFormData((current) => ({
      ...current,
      isDeceased: nextValue,
      dateOfDeath: nextValue ? current.dateOfDeath : '',
      allowPublicDisplayForLivingPerson: nextValue ? false : current.allowPublicDisplayForLivingPerson,
    }));
  };

  const buildFullName = () => {
    const parts = [formData.title, formData.firstName, formData.middleName, formData.lastName, formData.suffix]
      .map((part) => part.trim())
      .filter(Boolean);
    return parts.join(' ') || t('person.defaultName', { ns: 'canvas' });
  };
  const hasAddressValue = Boolean(
    formData.address1.trim() ||
      formData.address2.trim() ||
      formData.city.trim() ||
      formData.state.trim() ||
      formData.zip.trim() ||
      formData.country.trim()
  );

  const addressSummary = [
    formData.address1.trim(),
    formData.address2.trim(),
    [formData.city.trim(), formData.state.trim(), formData.zip.trim()].filter(Boolean).join(', '),
    formData.country.trim(),
  ]
    .filter(Boolean)
    .join(' • ');

  const openStoryEditor = () => {
    setStoryDraft(formData.longStory);
    setIsStoryEditorOpen(true);
  };

  const appendStoryPrompt = (template: string) => {
    setStoryDraft((current) => {
      const trimmedCurrent = current.trimEnd();
      return trimmedCurrent ? `${trimmedCurrent}\n\n${template}` : template;
    });
  };

  const saveStoryDraft = () => {
    setFieldValue('longStory', storyDraft);
    setIsStoryEditorOpen(false);
  };

  const clearPendingExitAction = () => {
    pendingExitActionRef.current = null;
    setShowUnsavedChangesDialog(false);
  };

  const runPendingExitAction = () => {
    const pendingExitAction = pendingExitActionRef.current;
    clearPendingExitAction();
    pendingExitAction?.();
  };

  const requestExit = (action: () => void) => {
    if (loading) {
      return;
    }

    if (hasUnsavedChanges) {
      pendingExitActionRef.current = action;
      setShowUnsavedChangesDialog(true);
      return;
    }

    action();
  };

  const submitChanges = async () => {
    setLoading(true);
    setError('');

    try {
      if (!formData.firstName.trim() || !formData.lastName.trim()) {
        setError('First name and last name are required');
        setLoading(false);
        return;
      }

      if (!formData.gender) {
        setError('Gender selection is required');
        setLoading(false);
        return;
      }

      if (formData.isDeceased && isBirthDateAfterDeathDate(formData.dateOfBirth, formData.dateOfDeath)) {
        setError('Date of birth must be on or before date of death');
        setLoading(false);
        return;
      }

      let dateOfBirth: string | undefined;
      if (formData.dateOfBirth.trim()) {
        dateOfBirth = serializeDateInputToIso(formData.dateOfBirth);
      }

      let dateOfDeath: string | undefined;
      if (formData.isDeceased && formData.dateOfDeath.trim()) {
        dateOfDeath = serializeDateInputToIso(formData.dateOfDeath);
      }

      const clearDateOfBirth =
        normalizedInitialValues.dateOfBirth !== '' && formData.dateOfBirth.trim() === '';
      const clearDateOfDeath =
        normalizedInitialValues.dateOfDeath !== '' &&
        (!formData.isDeceased || formData.dateOfDeath.trim() === '');

      const payload: UpdatePersonRequest = {
        firstName: formData.firstName.trim(),
        lastName: formData.lastName.trim(),
        gender: formData.gender || undefined,
        isDeceased: formData.isDeceased,
        publicVisibility: formData.publicVisibility,
        allowPublicDisplayForLivingPerson: formData.allowPublicDisplayForLivingPerson,
      };

      const isLiving = !formData.isDeceased;
      if (isLiving && formData.allowPublicDisplayForLivingPerson && !normalizedInitialValues.allowPublicDisplayForLivingPerson) {
        if (!livingVisibilityCapability?.canEnableLivingPersonPublicDisplay) {
          setError(livingVisibilityCapability?.blockedReason || t('person.livingPublicDisplayBlocked', { ns: 'canvas' }));
          setLoading(false);
          return;
        }

        const displayName = [formData.firstName.trim(), formData.lastName.trim()].filter(Boolean).join(' ');
        const warningText = t('person.livingPublicDisplayConsentWarning', {
          ns: 'canvas',
          name: displayName,
        });

        if (!window.confirm(warningText)) {
          setLoading(false);
          return;
        }

        payload.livingPersonPublicDisplayConsent = {
          accepted: true,
          consentMessageVersion: 'living-public-display-consent-v1',
          warningTextSnapshot: warningText,
          consentSource: 'EditPersonModal',
          affectedPersonIds: [person.id],
          affectedPersonNamesSnapshot: displayName ? [displayName] : [],
        };
      }

      if (formData.middleName.trim()) {
        payload.middleName = formData.middleName.trim();
      }
      if (formData.title.trim()) {
        payload.title = formData.title.trim();
      }
      if (formData.suffix.trim()) {
        payload.suffix = formData.suffix.trim();
      }
      if (formData.address1.trim()) {
        payload.address1 = formData.address1.trim();
      }
      if (formData.address2.trim()) {
        payload.address2 = formData.address2.trim();
      }
      if (formData.city.trim()) {
        payload.city = formData.city.trim();
      }
      if (formData.state.trim()) {
        payload.state = formData.state.trim();
      }
      if (formData.zip.trim()) {
        payload.zip = formData.zip.trim();
      }
      if (formData.country.trim()) {
        payload.country = formData.country.trim();
      }
      if (formData.phone.trim()) {
        payload.phone = formData.phone.trim();
      }
      if (formData.shortBio.trim()) {
        payload.shortDescription = formData.shortBio.trim();
      }
      if (formData.longStory.trim()) {
        payload.longStory = formData.longStory.trim();
      }
      if (formData.comments.trim()) {
        payload.comments = formData.comments.trim();
      }
      if (dateOfBirth) {
        payload.dateOfBirth = dateOfBirth;
      }
      if (clearDateOfBirth) {
        payload.clearDateOfBirth = true;
      }
      if (dateOfDeath) {
        payload.dateOfDeath = dateOfDeath;
      }
      const trimmedPlaceOfBirth = formData.placeOfBirth.trim();
      const trimmedPlaceOfDeath = formData.placeOfDeath.trim();
      const trimmedRestingPlace = formData.restingPlace.trim();

      if (normalizedInitialValues.placeOfBirth !== formData.placeOfBirth) {
        payload.placeOfBirth = trimmedPlaceOfBirth;
      }
      if (normalizedInitialValues.placeOfDeath !== formData.placeOfDeath) {
        payload.placeOfDeath = trimmedPlaceOfDeath;
      }
      if (normalizedInitialValues.restingPlace !== formData.restingPlace) {
        payload.restingPlace = trimmedRestingPlace;
      }
      if (clearDateOfDeath) {
        payload.clearDateOfDeath = true;
      }

      const hasDataChanges = hasUnsavedChanges || Boolean(isStoryEditorOpen && storyDraft !== formData.longStory);

      if (!hasDataChanges && !photoChanged) {
        onSuccess({
          didChange: false,
          person,
          photoChanged: false,
        });
        runPendingExitAction();
        return;
      }

      let updatedPerson: PersonDto = {
        ...person,
        ...payload,
        shortBio: payload.shortDescription ?? undefined,
        dateOfBirth: clearDateOfBirth ? undefined : dateOfBirth,
        dateOfDeath: clearDateOfDeath ? undefined : formData.isDeceased ? dateOfDeath : undefined,
        photoUrl: formData.photoUrl || undefined,
        photoCropX: cropData?.cropX,
        photoCropY: cropData?.cropY,
        photoCropWidth: cropData?.cropWidth,
        photoCropHeight: cropData?.cropHeight,
        photoCropZoom: cropData?.cropZoom,
      };

      if (hasDataChanges || (!pendingPhotoFile && photoChanged)) {
        const updatePayload: UpdatePersonRequest = { ...payload };

        if (!pendingPhotoFile && photoChanged) {
          if (!formData.photoUrl) {
            updatePayload.photoUrl = '';
          }

          updatePayload.photoCropX = cropData?.cropX ?? null;
          updatePayload.photoCropY = cropData?.cropY ?? null;
          updatePayload.photoCropWidth = cropData?.cropWidth ?? null;
          updatePayload.photoCropHeight = cropData?.cropHeight ?? null;
          updatePayload.photoCropZoom = cropData?.cropZoom ?? null;
        }

        const updateResponse = await peopleAPI.updatePerson(person.id, updatePayload);
        updatedPerson = extractPayload<PersonDto>(updateResponse.data);
      }

      if (pendingPhotoFile) {
        const photoFormData = new FormData();
        photoFormData.append('file', pendingPhotoFile);

        if (cropData) {
          photoFormData.append('cropX', cropData.cropX.toString());
          photoFormData.append('cropY', cropData.cropY.toString());
          photoFormData.append('cropWidth', cropData.cropWidth.toString());
          photoFormData.append('cropHeight', cropData.cropHeight.toString());
          photoFormData.append('cropZoom', cropData.cropZoom.toString());
        }

        const photoResponse = await peopleAPI.uploadPersonPhoto(person.id, photoFormData);
        const photoUrl = extractPayload<string>(photoResponse.data);
        updatedPerson = {
          ...updatedPerson,
          photoUrl: photoUrl || undefined,
          photoCropX: cropData?.cropX,
          photoCropY: cropData?.cropY,
          photoCropWidth: cropData?.cropWidth,
          photoCropHeight: cropData?.cropHeight,
          photoCropZoom: cropData?.cropZoom,
        };
      }

      trackEvent('person_edited');
      onSuccess({
        didChange: true,
        person: updatedPerson,
        photoChanged,
      });
      runPendingExitAction();
    } catch (err: any) {
      const errorMessage =
        err.response?.data?.detail ||
        err.response?.data?.message ||
        err.message ||
        'Failed to update person';
      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    await submitChanges();
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-[rgba(26,23,20,0.45)] px-4 py-6"
      onClick={() => requestExit(onClose)}
      data-testid="edit-person-backdrop"
    >
      <div
        className="flex max-h-[94vh] w-full max-w-5xl flex-col overflow-hidden rounded-[28px] border border-[#E2DDD8] bg-[#FFFFFF] shadow-[0_24px_60px_rgba(26,23,20,0.24)]"
        onClick={(e) => e.stopPropagation()}
        data-testid="edit-person-modal"
      >
        <div className={`relative border-b px-6 py-4 pr-16 lg:pr-24 ${brandClasses.subtleBorder} ${brandClasses.titleBarSurface}`}>
          <IconButton
            type="button"
            onClick={() => requestExit(onClose)}
            className="absolute right-6 top-4"
            aria-label={t('person.closeEditPersonAriaLabel', { ns: 'canvas' })}
          >
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M18 6L6 18" />
              <path d="M6 6l12 12" />
            </svg>
          </IconButton>
          <div className="flex min-w-0 items-start gap-4">
            <div className="min-w-[104px]">
            <PhotoUploadField
              personId={person.id}
              initialPhotoUrl={formData.photoUrl}
              initialCropData={cropData}
              onPhotoUrlChange={(url) => setFieldValue('photoUrl', url)}
              onPhotoFileChange={(file) => setPendingPhotoFile(file)}
              onCropDataChange={(data) => setCropData(data)}
              onPhotoSaved={({ photoUrl, cropData: savedCropData }) => {
                setPhotoChanged(true);
                setFieldValue('photoUrl', photoUrl);
                setCropData(savedCropData);
              }}
              disabled={loading}
              readOnly={true}
              compact={true}
              hideUrlField={true}
              uploadLabel="Upload"
            />
            </div>

            <div className="min-w-0 flex-1">
              <div className="flex flex-col gap-3">
                <div className="flex flex-col gap-3 lg:flex-row lg:items-start lg:justify-between">
                  <p className="font-['Playfair_Display',Georgia,serif] text-[34px] font-semibold text-[#1A1714]">
                    {buildFullName()}
                  </p>
                  <div className="flex justify-end pr-4 lg:pr-8">
                    <Button
                      type="submit"
                      form="edit-person-form"
                      size="lg"
                      disabled={loading}
                      data-testid="edit-person-save-button"
                      className="min-w-[118px] rounded-[18px]"
                    >
                      {loading ? t('person.savingLabel', { ns: 'canvas' }) : t('person.saveLabel', { ns: 'canvas' })}
                    </Button>
                  </div>
                </div>
                <p className="mt-1 text-sm text-[#6B6560]">
                  {t('person.biographyHint', { ns: 'canvas' })}
                </p>
                  <div className="mt-3">
                    <SegmentedControl
                      value={formData.isDeceased ? 'deceased' : 'living'}
                      onChange={(value) => toggleDeceasedState(value === 'deceased')}
                      items={[
                        { value: 'living', label: t('person.status.living', { ns: 'canvas' }) },
                        { value: 'deceased', label: t('person.status.deceased', { ns: 'canvas' }) },
                      ]}
                    />
                  </div>
              </div>
            </div>
          </div>
        </div>

        <form id="edit-person-form" onSubmit={handleSubmit} className="flex min-h-0 flex-1 flex-col">
          <div className="flex-1 overflow-y-auto px-6 py-6">
            <div className="mx-auto grid max-w-6xl gap-6 xl:grid-cols-[minmax(0,1.3fr)_minmax(320px,0.9fr)]">
              <div className="space-y-6">
                {error && (
                  <div className="rounded-xl border border-[#F7C1C1] bg-[#FCEBEB] px-4 py-3 text-sm text-[#A32D2D]">
                    {error}
                  </div>
                )}

                <section className="rounded-[24px] border border-[#E2DDD8] bg-[#FFFFFF] p-5 shadow-[0_10px_30px_rgba(26,23,20,0.05)]">
                  <div className={sectionLabelClassName}>{t('person.sections.identity', { ns: 'canvas' })}</div>
                  <div className="mt-4 grid gap-4 md:grid-cols-2">
                    <label className="flex flex-col gap-1">
                      <span className={fieldLabelClassName}>{t('person.fields.titlePrefix', { ns: 'canvas' })}</span>
                      <input
                        type="text"
                        value={formData.title}
                        onChange={(e) => setFieldValue('title', e.target.value)}
                        placeholder={t('person.fields.titlePrefixPlaceholder', { ns: 'canvas' })}
                        className={inputClassName}
                      />
                    </label>
                    <label className="flex flex-col gap-1">
                      <span className={fieldLabelClassName}>{t('person.fields.suffix', { ns: 'canvas' })}</span>
                      <input
                        type="text"
                        value={formData.suffix}
                        onChange={(e) => setFieldValue('suffix', e.target.value)}
                        placeholder={t('person.fields.suffixPlaceholder', { ns: 'canvas' })}
                        className={inputClassName}
                      />
                    </label>
                    <label className="flex flex-col gap-1">
                      <span className={fieldLabelClassName}>
                        {t('person.fields.firstName', { ns: 'canvas' })} <span className="text-[#A32D2D]">*</span>
                      </span>
                      <input
                        type="text"
                        required
                        autoFocus
                        value={formData.firstName}
                        onChange={(e) => setFieldValue('firstName', e.target.value)}
                        placeholder={t('person.fields.firstNamePlaceholder', { ns: 'canvas' })}
                        data-testid="edit-person-first-name-input"
                        className={inputClassName}
                      />
                    </label>
                    <label className="flex flex-col gap-1">
                      <span className={fieldLabelClassName}>{t('person.fields.middleName', { ns: 'canvas' })}</span>
                      <input
                        type="text"
                        value={formData.middleName}
                        onChange={(e) => setFieldValue('middleName', e.target.value)}
                        placeholder={t('person.fields.middleNamePlaceholder', { ns: 'canvas' })}
                        className={inputClassName}
                      />
                    </label>
                    <label className="flex flex-col gap-1 md:col-span-2">
                      <span className={fieldLabelClassName}>
                        {t('person.fields.lastName', { ns: 'canvas' })} <span className="text-[#A32D2D]">*</span>
                      </span>
                      <input
                        type="text"
                        required
                        value={formData.lastName}
                        onChange={(e) => setFieldValue('lastName', e.target.value)}
                        placeholder={t('person.fields.lastNamePlaceholder', { ns: 'canvas' })}
                        data-testid="edit-person-last-name-input"
                        className={inputClassName}
                      />
                    </label>
                  </div>
                </section>

                <section className="rounded-[24px] border border-[#E2DDD8] bg-[#FFFFFF] p-5 shadow-[0_10px_30px_rgba(26,23,20,0.05)]">
                  <div className={sectionLabelClassName}>{t('person.sectionHeaders.vitalEvents', { ns: 'canvas' })}</div>
                    <div className="mt-4 grid gap-4 md:grid-cols-2">
                      <label className="flex flex-col gap-1">
                        <span className={fieldLabelClassName}>
                          {t('person.fields.gender', { ns: 'canvas' })} <span className="text-[#A32D2D]">*</span>
                        </span>
                      <select
                        value={formData.gender}
                        onChange={(e) => setFieldValue('gender', e.target.value)}
                        aria-label={t('person.fields.gender', { ns: 'canvas' })}
                        className={inputClassName}
                      >
                        <option value="" disabled>
                          {t('person.fields.genderPlaceholder', { ns: 'canvas' })}
                        </option>
                        <option value="Unspecified">{t('person.gender.unspecified', { ns: 'canvas' })}</option>
                        <option value="Male">{t('person.gender.male', { ns: 'canvas' })}</option>
                        <option value="Female">{t('person.gender.female', { ns: 'canvas' })}</option>
                        <option value="Other">{t('person.gender.other', { ns: 'canvas' })}</option>
                      </select>
                    </label>
                    <div className="flex items-end rounded-2xl border border-[#E2DDD8] bg-[#F9F7F4] px-4 py-3 text-sm text-[#6B6560]">
                      <p>{t('person.deceasedHint', { ns: 'canvas' })}</p>
                    </div>
                    <label className="flex flex-col gap-1 md:col-span-2">
                      <span className={fieldLabelClassName}>{t('person.fields.publicVisibility', { ns: 'canvas' })}</span>
                      <select
                        value={formData.publicVisibility}
                        onChange={(e) => {
                          const nextValue = e.target.value as 'Public' | 'Masked';
                          setFieldValue('publicVisibility', nextValue);
                          if (nextValue === 'Masked') {
                            setFieldValue('allowPublicDisplayForLivingPerson', false);
                          }
                        }}
                        aria-label={t('person.fields.publicVisibility', { ns: 'canvas' })}
                        className={inputClassName}
                      >
                        <option value="Public">{t('person.publicVisibility.public', { ns: 'canvas' })}</option>
                        <option value="Masked">{t('person.publicVisibility.masked', { ns: 'canvas' })}</option>
                      </select>
                    </label>
                    <div className="rounded-2xl border border-[#E2DDD8] bg-[#F9F7F4] px-4 py-3 text-sm text-[#6B6560] md:col-span-2">
                      <p>{t('person.publicVisibilityHint', { ns: 'canvas' })}</p>
                      {!formData.isDeceased ? (
                        <p className="mt-2 font-medium text-[#7A3E1D]">{t('person.livingVisibilityWarning', { ns: 'canvas' })}</p>
                      ) : null}
                    </div>
                    {!formData.isDeceased && formData.publicVisibility === 'Public' ? (
                      <label className="flex items-start gap-3 rounded-2xl border border-[#DCC9A3] bg-[#FFF8E8] px-4 py-3 text-sm text-[#5D4A1F] md:col-span-2">
                        <input
                          type="checkbox"
                          checked={formData.allowPublicDisplayForLivingPerson}
                          onChange={(e) => setFieldValue('allowPublicDisplayForLivingPerson', e.target.checked)}
                          disabled={!livingVisibilityCapability?.canEnableLivingPersonPublicDisplay}
                          className="mt-0.5 h-4 w-4 rounded border-[#B89C5B]"
                        />
                        <span>
                          <span className="block font-medium">{t('person.livingPublicDisplayToggle', { ns: 'canvas' })}</span>
                          <span className="mt-1 block text-xs text-[#6B6560]">
                            {livingVisibilityCapability?.canEnableLivingPersonPublicDisplay
                              ? t('person.livingPublicDisplayHelp', { ns: 'canvas' })
                              : livingVisibilityCapability?.blockedReason || t('person.livingPublicDisplayBlocked', { ns: 'canvas' })}
                          </span>
                        </span>
                      </label>
                    ) : null}
                    <label className="flex flex-col gap-1">
                      <span className={fieldLabelClassName}>{t('person.fields.dateOfBirth', { ns: 'canvas' })}</span>
                      <div className={dateInputWrapperClassName}>
                        <input
                          type="date"
                          aria-label={t('person.fields.dateOfBirth', { ns: 'canvas' })}
                          value={formData.dateOfBirth}
                          onChange={(e) => setFieldValue('dateOfBirth', e.target.value)}
                          className={`${inputClassName} ${formData.dateOfBirth ? 'pr-16' : ''}`}
                        />
                        {formData.dateOfBirth && (
                          <button
                            type="button"
                            onClick={() => setFieldValue('dateOfBirth', '')}
                            className={clearDateButtonClassName}
                            aria-label={t('person.fields.clearDateOfBirth', { ns: 'canvas' })}
                          >
                            Clear
                          </button>
                        )}
                      </div>
                    </label>
                    <label className="flex flex-col gap-1">
                      <span className={fieldLabelClassName}>{t('person.fields.dateOfDeath', { ns: 'canvas' })}</span>
                      <div className={dateInputWrapperClassName}>
                        <input
                          type="date"
                          aria-label={t('person.fields.dateOfDeath', { ns: 'canvas' })}
                          value={formData.dateOfDeath}
                          onChange={(e) => setFieldValue('dateOfDeath', e.target.value)}
                          disabled={!formData.isDeceased}
                          className={`${inputClassName} ${formData.dateOfDeath ? 'pr-16' : ''} disabled:cursor-not-allowed disabled:bg-[#EFEAE3] disabled:text-[#8D867E]`}
                        />
                        {formData.isDeceased && formData.dateOfDeath && (
                          <button
                            type="button"
                            onClick={() => setFieldValue('dateOfDeath', '')}
                            className={clearDateButtonClassName}
                            aria-label={t('person.fields.clearDateOfDeath', { ns: 'canvas' })}
                          >
                            Clear
                          </button>
                        )}
                      </div>
                    </label>
                    <label className="flex flex-col gap-1 md:col-span-2">
                      <span className={fieldLabelClassName}>{t('person.fields.placeOfBirth', { ns: 'canvas' })}</span>
                      <input
                        type="text"
                        value={formData.placeOfBirth}
                        onChange={(e) => setFieldValue('placeOfBirth', e.target.value)}
                        placeholder={t('person.fields.placeOfBirthPlaceholder', { ns: 'canvas' })}
                        className={inputClassName}
                      />
                    </label>
                    <label className="flex flex-col gap-1">
                      <span className={fieldLabelClassName}>{t('person.fields.placeOfDeath', { ns: 'canvas' })}</span>
                      <input
                        type="text"
                        value={formData.placeOfDeath}
                        onChange={(e) => setFieldValue('placeOfDeath', e.target.value)}
                        placeholder={t('person.fields.placeOfDeathPlaceholder', { ns: 'canvas' })}
                        disabled={!formData.isDeceased}
                        className={`${inputClassName} disabled:cursor-not-allowed disabled:bg-[#EFEAE3] disabled:text-[#8D867E]`}
                      />
                    </label>
                    <label className="flex flex-col gap-1">
                      <span className={fieldLabelClassName}>{t('person.fields.restingPlace', { ns: 'canvas' })}</span>
                      <input
                        type="text"
                        value={formData.restingPlace}
                        onChange={(e) => setFieldValue('restingPlace', e.target.value)}
                        placeholder={t('person.fields.restingPlacePlaceholder', { ns: 'canvas' })}
                        className={inputClassName}
                      />
                    </label>
                  </div>
                </section>

                <section className="rounded-[24px] border border-[#E2DDD8] bg-[#FFFFFF] p-5 shadow-[0_10px_30px_rgba(26,23,20,0.05)]">
                  <div className={sectionLabelClassName}>{t('person.sectionHeaders.biography', { ns: 'canvas' })}</div>
                  <div className="mt-4 space-y-4">
                    <label className="flex flex-col gap-1">
                      <span className={fieldLabelClassName}>{t('person.shortDescLabel', { ns: 'canvas' })}</span>
                      <textarea
                        value={formData.shortBio}
                        onChange={(e) => setFieldValue('shortBio', e.target.value)}
                        placeholder={t('person.fields.shortBioPlaceholder', { ns: 'canvas' })}
                        className="min-h-[92px] w-full rounded-xl border border-[#E2DDD8] bg-[#F9F7F4] px-3 py-2.5 text-sm text-[#1A1714] outline-none transition focus:border-[#185FA5] focus:bg-[#FFFFFF] focus:ring-4 focus:ring-[rgba(24,95,165,0.08)]"
                        maxLength={255}
                      />
                    </label>
                    <div className="flex flex-col gap-2">
                      <div className="flex items-center justify-between gap-3">
                        <span className={fieldLabelClassName}>{t('person.storyLabel', { ns: 'canvas' })}</span>
                        <button
                          type="button"
                          onClick={openStoryEditor}
                          className="rounded-xl border border-[#C8C3BB] bg-[#FFFFFF] px-3 py-2 text-xs font-medium text-[#1A1714] transition hover:bg-[#EEF4FB]"
                        >
                          {formData.longStory.trim() ? t('person.storyEditLabel', { ns: 'canvas' }) : t('person.storyOpenLabel', { ns: 'canvas' })}
                        </button>
                      </div>
                      <div className="rounded-2xl border border-[#E2DDD8] bg-[#F9F7F4] px-4 py-4">
                        <p className="whitespace-pre-line text-sm leading-6 text-[#4A443E]">{formData.longStory.trim() ? (formData.longStory.trim().length > 220 ? `${formData.longStory.trim().slice(0, 220).trimEnd()}...` : formData.longStory.trim()) : t('person.storyEmpty', { ns: 'canvas' })}</p>
                        <div className="mt-3 flex flex-wrap items-center gap-2 text-xs text-[#6B6560]">
                          <span className="rounded-full bg-[#FFFFFF] px-2.5 py-1">
                            {storyWordCount} {storyWordCount === 1 ? t('person.storyWordSingular', { ns: 'canvas' }) : t('person.storyWordPlural', { ns: 'canvas' })}
                          </span>
                          <span>{t('person.storyEditorHint', { ns: 'canvas' })}</span>
                        </div>
                      </div>
                    </div>
                    <label className="flex flex-col gap-1">
                      <span className={fieldLabelClassName}>{t('person.internalNotesLabel', { ns: 'canvas' })}</span>
                      <textarea
                        value={formData.comments}
                        onChange={(e) => setFieldValue('comments', e.target.value)}
                        placeholder={t('person.fields.commentsPlaceholder', { ns: 'canvas' })}
                        className="min-h-[92px] w-full rounded-xl border border-[#E2DDD8] bg-[#F9F7F4] px-3 py-2.5 text-sm text-[#1A1714] outline-none transition focus:border-[#185FA5] focus:bg-[#FFFFFF] focus:ring-4 focus:ring-[rgba(24,95,165,0.08)]"
                      />
                    </label>
                  </div>
                </section>
              </div>

              <div className="space-y-6">
                <section className="rounded-[24px] border border-[#E2DDD8] bg-[#FFFFFF] p-5 shadow-[0_10px_30px_rgba(26,23,20,0.05)]">
                  <div className={sectionLabelClassName}>{t('person.sectionHeaders.address', { ns: 'canvas' })}</div>
                  <div className="mt-4 space-y-4">
                    <div className="rounded-2xl border border-[#E2DDD8] bg-[#F9F7F4] px-4 py-4">
                      <p className="text-sm text-[#6B6560]">
                        {hasAddressValue
                          ? addressSummary
                          : t('person.addressHint', { ns: 'canvas' })}
                      </p>
                      <button
                        type="button"
                        onClick={() => setIsAddressExpanded((current) => !current)}
                        className="mt-3 rounded-xl border border-[#C8C3BB] bg-[#FFFFFF] px-4 py-2 text-sm font-medium text-[#1A1714] transition hover:bg-[#EEF4FB]"
                      >
                        {isAddressExpanded ? t('person.addressHide', { ns: 'canvas' }) : hasAddressValue ? t('person.addressEdit', { ns: 'canvas' }) : t('person.addressAdd', { ns: 'canvas' })}
                      </button>
                    </div>

                    {isAddressExpanded && (
                      <div className="space-y-4">
                        <label className="flex flex-col gap-1">
                          <span className={fieldLabelClassName}>{t('person.addressLine1Label', { ns: 'canvas' })}</span>
                          <input
                            type="text"
                            value={formData.address1}
                            onChange={(e) => setFieldValue('address1', e.target.value)}
                            placeholder={t('person.fields.streetAddressPlaceholder', { ns: 'canvas' })}
                            className={inputClassName}
                          />
                        </label>
                        <label className="flex flex-col gap-1">
                          <span className={fieldLabelClassName}>{t('person.addressLine2Label', { ns: 'canvas' })}</span>
                          <input
                            type="text"
                            value={formData.address2}
                            onChange={(e) => setFieldValue('address2', e.target.value)}
                            placeholder={t('person.fields.address2Placeholder', { ns: 'canvas' })}
                            className={inputClassName}
                          />
                        </label>
                        <label className="flex flex-col gap-1">
                          <span className={fieldLabelClassName}>{t('person.cityLabel', { ns: 'canvas' })}</span>
                          <input
                            type="text"
                            value={formData.city}
                            onChange={(e) => setFieldValue('city', e.target.value)}
                            placeholder={t('person.fields.city', { ns: 'canvas' })}
                            className={inputClassName}
                          />
                        </label>
                        <div className="grid gap-4 md:grid-cols-2">
                          <label className="flex flex-col gap-1">
                            <span className={fieldLabelClassName}>{t('person.stateLabel', { ns: 'canvas' })}</span>
                            <input
                              type="text"
                              value={formData.state}
                              onChange={(e) => setFieldValue('state', e.target.value)}
                              placeholder={t('person.fields.state', { ns: 'canvas' })}
                              className={inputClassName}
                            />
                          </label>
                          <label className="flex flex-col gap-1">
                            <span className={fieldLabelClassName}>{t('person.zipLabel', { ns: 'canvas' })}</span>
                            <input
                              type="text"
                              value={formData.zip}
                              onChange={(e) => setFieldValue('zip', e.target.value)}
                              placeholder={t('person.fields.zip', { ns: 'canvas' })}
                              className={inputClassName}
                            />
                          </label>
                        </div>
                        <label className="flex flex-col gap-1">
                          <span className={fieldLabelClassName}>{t('person.countryLabel', { ns: 'canvas' })}</span>
                          <input
                            type="text"
                            value={formData.country}
                            onChange={(e) => setFieldValue('country', e.target.value)}
                            placeholder={t('person.fields.country', { ns: 'canvas' })}
                            className={inputClassName}
                          />
                        </label>
                      </div>
                    )}
                  </div>
                </section>

                <section className="rounded-[24px] border border-[#E2DDD8] bg-[#FFFFFF] p-5 shadow-[0_10px_30px_rgba(26,23,20,0.05)]">
                  <div className={sectionLabelClassName}>{t('person.sectionHeaders.contact', { ns: 'canvas' })}</div>
                  <div className="mt-4 space-y-4">
                    <label className="flex flex-col gap-1">
                      <span className={fieldLabelClassName}>{t('person.phoneLabel', { ns: 'canvas' })}</span>
                      <input
                        type="tel"
                        value={formData.phone}
                        onChange={(e) => setFieldValue('phone', e.target.value)}
                        placeholder={t('person.phonePlaceholder', { ns: 'canvas' })}
                        className={inputClassName}
                      />
                    </label>
                  </div>
                </section>

                <section className="rounded-[24px] border border-[#E2DDD8] bg-[#FFFFFF] p-5 shadow-[0_10px_30px_rgba(26,23,20,0.05)]">
                  <div className={sectionLabelClassName}>{t('person.sectionHeaders.familyLinks', { ns: 'canvas' })}</div>
                  <div className="mt-4 space-y-3">
                    {groupedFamilyLinks.length > 0 ? (
                      groupedFamilyLinks.map(({ groupLabel, links }) => (
                        <div key={groupLabel} className="space-y-3">
                          <div className="flex items-center justify-between">
                            <p className="text-xs font-semibold uppercase tracking-[0.08em] text-[#6B6560]">
                            {familyGroupLabels[groupLabel] ?? groupLabel}
                            </p>
                            <span className="rounded-full bg-[#EEF4FB] px-2.5 py-1 text-xs font-medium text-[#185FA5]">
                              {links.length}
                            </span>
                          </div>
                          {links.map((link) => (
                            <div
                              key={link.relationshipId}
                              className="rounded-2xl border border-[#E2DDD8] bg-[#F9F7F4] px-4 py-4"
                            >
                              <div className="flex items-start justify-between gap-3">
                                <div className="min-w-0">
                                  <p className="text-sm font-semibold text-[#1A1714]">{link.relatedPersonName}</p>
                                  <p className="mt-1 text-sm text-[#6B6560]">{link.relationToPerson}</p>
                                </div>
                <span className="rounded-full border border-[#D7D3CC] bg-[#FFFFFF] px-3 py-1 text-xs font-medium text-[#6B6560]">
                                  {link.canEdit || link.canDelete ? t('person.editableOnCanvas', { ns: 'canvas' }) : t('person.viewOnly', { ns: 'canvas' })}
                                </span>
                              </div>
                              <div className="mt-3 flex flex-wrap gap-2">
                                {onEditFamilyLink && (link.canEdit || link.canDelete) && (
                                  <button
                                    type="button"
                                    onClick={() => requestExit(() => onEditFamilyLink(link.relationshipId))}
                                    className="rounded-xl border border-[#C8C3BB] bg-[#FFFFFF] px-4 py-2 text-sm font-medium text-[#1A1714] transition hover:bg-[#EEF4FB]"
                                  >
                                    {t('person.editLinkLabel', { ns: 'canvas' })}
                                  </button>
                                )}
                                {onJumpToFamilyLink && (
                                  <button
                                    type="button"
                                    onClick={() => requestExit(() => onJumpToFamilyLink(link.relationshipId))}
                                    className="rounded-xl border border-[#C8C3BB] bg-[#FFFFFF] px-4 py-2 text-sm font-medium text-[#1A1714] transition hover:bg-[#EEF4FB]"
                                  >
                                    {link.canEdit || link.canDelete ? t('person.showOnCanvas', { ns: 'canvas' }) : t('person.viewOnCanvas', { ns: 'canvas' })}
                                  </button>
                                )}
                              </div>
                            </div>
                          ))}
                        </div>
                      ))
                    ) : (
                      <div className="rounded-2xl border border-dashed border-[#D7D3CC] bg-[#F9F7F4] px-4 py-4 text-sm text-[#6B6560]">
                        {t('person.familyLinksNoLinks', { ns: 'canvas' })}
                      </div>
                    )}

                    {onAddFamilyLinkByType && (
                      <div className="rounded-2xl border border-[#E2DDD8] bg-[#F9F7F4] px-4 py-4">
                        <p className="text-xs font-semibold uppercase tracking-[0.08em] text-[#6B6560]">
                          {t('person.addNewRelatedPerson', { ns: 'canvas' })}
                        </p>
                        <p className="mt-2 text-sm text-[#6B6560]">
                          {t('person.addNewRelatedPersonHint', { ns: 'canvas' })}
                        </p>
                        <div className="mt-3 flex flex-wrap gap-2">
                          <button
                            type="button"
                            onClick={() => requestExit(() => onAddFamilyLinkByType('parent'))}
                            className="rounded-full border border-[#C8C3BB] bg-[#FFFFFF] px-3 py-2 text-sm font-medium text-[#1A1714] transition hover:bg-[#EEF4FB]"
                          >
                            {t('person.addParent', { ns: 'canvas' })}
                          </button>
                          <button
                            type="button"
                            onClick={() => requestExit(() => onAddFamilyLinkByType('spouse'))}
                            className="rounded-full border border-[#C8C3BB] bg-[#FFFFFF] px-3 py-2 text-sm font-medium text-[#1A1714] transition hover:bg-[#EEF4FB]"
                          >
                            {t('person.addPartner', { ns: 'canvas' })}
                          </button>
                          <button
                            type="button"
                            onClick={() => requestExit(() => onAddFamilyLinkByType('child'))}
                            className="rounded-full border border-[#C8C3BB] bg-[#FFFFFF] px-3 py-2 text-sm font-medium text-[#1A1714] transition hover:bg-[#EEF4FB]"
                          >
                            {t('person.addChild', { ns: 'canvas' })}
                          </button>
                        </div>
                      </div>
                    )}

                    {onAddExistingFamilyLink && (
                      <div className="rounded-2xl border border-[#E2DDD8] bg-[#FFFFFF] px-4 py-4">
                        <p className="text-xs font-semibold uppercase tracking-[0.08em] text-[#6B6560]">
                          {t('person.linkExistingPerson', { ns: 'canvas' })}
                        </p>
                        <p className="mt-2 text-sm text-[#6B6560]">
                          {t('person.linkExistingPersonHint', { ns: 'canvas' })}
                        </p>
                        <button
                          type="button"
                          onClick={() => requestExit(onAddExistingFamilyLink)}
                          className="mt-3 rounded-xl border border-[#C8C3BB] px-4 py-3 text-sm font-medium text-[#1A1714] transition hover:bg-[#F9F7F4]"
                        >
                          {t('person.linkExistingPersonLabel', { ns: 'canvas' })}
                        </button>
                      </div>
                    )}
                  </div>
                </section>
              </div>
            </div>
          </div>

          <ModalFooter actionRowClassName="mx-auto max-w-6xl">
              <Button
                type="button"
                variant="outline"
                onClick={() => requestExit(onClose)}
                className="sm:min-w-[160px]"
              >
                {t('buttons.cancel', { ns: 'common' })}
              </Button>
              <Button
                type="submit"
                form="edit-person-form"
                disabled={loading}
                className="sm:min-w-[220px]"
              >
                {loading ? t('person.savingEllipsis', { ns: 'canvas' }) : t('person.saveChangesLabel', { ns: 'canvas' })}
              </Button>
          </ModalFooter>
        </form>
      </div>

      {isStoryEditorOpen && (
        <div
          className="absolute inset-0 z-10 flex items-center justify-center bg-[rgba(26,23,20,0.35)] px-4 py-6"
          onClick={() => setIsStoryEditorOpen(false)}
        >
          <div
            className="flex max-h-[88vh] w-full max-w-3xl flex-col overflow-hidden rounded-[28px] border border-[#E2DDD8] bg-[#FFFFFF] shadow-[0_24px_60px_rgba(26,23,20,0.24)]"
            onClick={(e) => e.stopPropagation()}
          >
            <ModalHeader
              eyebrow={t('person.storyEditorTitle', { ns: 'canvas' })}
              title={<h3 className="font-['Playfair_Display',Georgia,serif] text-[26px] font-semibold text-[#1A1714]">{t('person.storyEditorSubtitle', { ns: 'canvas' })}</h3>}
              description={t('person.storyEditor.hint', { ns: 'canvas' })}
              onClose={() => setIsStoryEditorOpen(false)}
              closeAriaLabel={t('person.storyEditor.closeAriaLabel', { ns: 'canvas' })}
            />

            <div className="flex-1 overflow-y-auto px-6 py-6">
              <div className="rounded-[24px] border border-[#E2DDD8] bg-[linear-gradient(135deg,#EEF4FB_0%,#F6FAFE_100%)] px-5 py-4">
                <p className="text-sm font-medium text-[#1A1714]">{t('person.storyStartersTitle', { ns: 'canvas' })}</p>
                <p className="mt-1 text-sm text-[#6B6560]">
                  {t('person.storyStartersSubtitle', { ns: 'canvas' })}
                </p>
                <div className="mt-4 flex flex-wrap gap-2">
                  {STORY_PROMPTS.map((prompt) => (
                    <button
                      key={prompt.label}
                      type="button"
                      onClick={() => appendStoryPrompt(prompt.template)}
                      className={storyPromptButtonClassName}
                    >
                      {t('person.storyStarterAddPrefix', { ns: 'canvas' })} {t(`person.storyPrompts.${prompt.key}`, { ns: 'canvas', defaultValue: prompt.label })}
                    </button>
                  ))}
                </div>
              </div>

              <div className="mt-5 grid gap-5 lg:grid-cols-[minmax(0,1fr)_240px]">
                <div className="space-y-2">
                  <label className="flex flex-col gap-2">
                    <span className={fieldLabelClassName}>{t('person.storyEditor.longStoryLabel', { ns: 'canvas' })}</span>
                    <textarea
                      aria-label={t('person.storyEditor.longStoryLabel', { ns: 'canvas' })}
                      value={storyDraft}
                      onChange={(e) => setStoryDraft(e.target.value)}
                      placeholder={t('person.fields.longStoryPlaceholder', { ns: 'canvas' })}
                      className="min-h-[360px] w-full rounded-[20px] border border-[#E2DDD8] bg-[#F9F7F4] px-4 py-4 text-sm leading-6 text-[#1A1714] outline-none transition focus:border-[#185FA5] focus:bg-[#FFFFFF] focus:ring-4 focus:ring-[rgba(24,95,165,0.08)]"
                    />
                  </label>
                </div>

                <div className="space-y-4">
                  <div className="rounded-[20px] border border-[#E2DDD8] bg-[#F9F7F4] px-4 py-4">
                    <p className="text-xs font-semibold uppercase tracking-[0.08em] text-[#6B6560]">{t('person.writingSnapshotTitle', { ns: 'canvas' })}</p>
                    <div className="mt-3 space-y-2 text-sm text-[#4A443E]">
                      <p>{storyDraft.trim() ? storyDraft.trim().split(/\s+/).length : 0} {storyDraft.trim() && storyDraft.trim().split(/\s+/).length === 1 ? t('person.storyWordSingular', { ns: 'canvas' }) : t('person.storyWordPlural', { ns: 'canvas' })}</p>
                      <p>{storyDraft.trim() ? t('person.storySectionsDrafted_other', { count: storyDraft.trim().split(/\n+/).length, ns: 'canvas' }) : t('person.storyNoSections', { ns: 'canvas' })}</p>
                    </div>
                  </div>

                  <div className="rounded-[20px] border border-[#E2DDD8] bg-[#F9F7F4] px-4 py-4">
                    <p className="text-xs font-semibold uppercase tracking-[0.08em] text-[#6B6560]">{t('person.tipsTitle', { ns: 'canvas' })}</p>
                    <ul className="mt-3 space-y-2 text-sm leading-6 text-[#4A443E]">
                      <li>{t('person.storyEditorTip1', { ns: 'canvas' })}</li>
                      <li>{t('person.storyEditorTip2', { ns: 'canvas' })}</li>
                      <li>{t('person.storyEditorTip3', { ns: 'canvas' })}</li>
                    </ul>
                  </div>
                </div>
              </div>
            </div>

            <ModalFooter actionRowClassName="sm:justify-start">
                <button
                  type="button"
                  onClick={() => setStoryDraft('')}
                  className="rounded-xl border border-[#C8C3BB] px-5 py-3 text-sm font-medium text-[#1A1714] transition hover:bg-[#F9F7F4] sm:min-w-[150px]"
                >
                  {t('person.clearDraft', { ns: 'canvas' })}
                </button>
                <button
                  type="button"
                  onClick={() => setIsStoryEditorOpen(false)}
                  className="rounded-xl border border-[#C8C3BB] px-5 py-3 text-sm font-medium text-[#1A1714] transition hover:bg-[#F9F7F4] sm:min-w-[150px]"
                >
                  {t('person.backToProfile', { ns: 'canvas' })}
                </button>
                <button
                  type="button"
                  onClick={saveStoryDraft}
                  className="rounded-xl bg-[#185FA5] px-5 py-3 text-sm font-medium text-white transition hover:bg-[#0C447C] sm:min-w-[220px]"
                >
                  {t('person.useThisStory', { ns: 'canvas' })}
                </button>
            </ModalFooter>
          </div>
        </div>
      )}

      <UnsavedChangesDialog
        isOpen={showUnsavedChangesDialog}
        onSave={() => {
          void submitChanges();
        }}
        onDiscard={runPendingExitAction}
        onCancel={clearPendingExitAction}
        saving={loading}
      />
    </div>
  );
};
